import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building2, Edit, Lock, Bell, Linkedin, Chrome, CreditCard, Calendar, TrendingUp, Bookmark, Clock, MessageSquare, LogOut, Trash2, CheckCircle, XCircle, AlertCircle, Save, X, RefreshCw, User as UserIcon, Check } from 'lucide-react';
import axios from 'axios';
import SecondaryFooter from '../Common/SecondaryFooter';
import './ProfilePage.css';
import profileService from '../../service/profile.service';
import config from '../../environment/config';
import { submitDemoRequest, transformDemoRequestData } from '../../service/demo-request.service';
import { getMySubscription } from "../../service/subscription.service";
const API_BASE_URL = config.API_URL;


// ============= DEMO MODAL COMPONENT =============
const DemoRequestModal = ({ isOpen, onClose, productName, productId }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    userType: '',
    mobileNumber: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.userType) {
      newErrors.userType = 'Please select a user type';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber.trim())) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const backendPayload = transformDemoRequestData(formData, productName);
      await submitDemoRequest(backendPayload);

      const web3Payload = {
        ...formData,
        productName: productName,
        access_key: 'ffe9b71b-4e54-41ed-bb2a-c52a8b9d9ce4'
      };

      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(web3Payload)
      });

      setSubmitSuccess(true);

      setTimeout(() => {
        setFormData({
          firstName: '',
          userType: '',
          mobileNumber: '',
          email: '',
          message: ''
        });
        setSubmitSuccess(false);
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting demo request:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.title ||
        error.message ||
        'Something went wrong. Please try again.';
      setErrors({ submit: `❌ ${errorMessage}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        firstName: '',
        userType: '',
        mobileNumber: '',
        email: '',
        message: ''
      });
      setErrors({});
      setSubmitSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="demo-modal-overlay" onClick={handleClose}>
      <div className="demo-modal-container" onClick={e => e.stopPropagation()}>
        <button
          className="demo-modal-close"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        <div className="demo-modal-header">
          <h2>Request Free Demo</h2>
          <p>Fill in your details and we'll get back to you shortly</p>
          {productName && (
            <div className="selected-product-badge">
              Product: <strong>{productName}</strong>
            </div>
          )}
        </div>

        {submitSuccess ? (
          <div className="demo-success-message">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h3>Request Submitted Successfully!</h3>
            <p>✅ Thank you for requesting a demo! We will get back to you soon.</p>
          </div>
        ) : (
          <form className="demo-modal-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'error' : ''}
                placeholder="Enter your first name"
                disabled={isSubmitting}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="userType">
                User Type <span className="required">*</span>
              </label>
              <select
                id="userType"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                className={errors.userType ? 'error' : ''}
                disabled={isSubmitting}
              >
                <option value="">Select Type</option>
                <option value="Individual">Individual</option>
                <option value="Organisation">Organisation</option>
              </select>
              {errors.userType && (
                <span className="error-message">{errors.userType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="mobileNumber">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                className={errors.mobileNumber ? 'error' : ''}
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
                disabled={isSubmitting}
              />
              {errors.mobileNumber && (
                <span className="error-message">{errors.mobileNumber}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email ID <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email address"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your requirements (optional)"
                rows="4"
                disabled={isSubmitting}
              />
            </div>

            {errors.submit && (
              <div className="submit-error">{errors.submit}</div>
            )}

            <button
              type="submit"
              className="demo-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Demo'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const ProfilePage = () => {

  const [profile, setProfile] = useState({
    loginId: localStorage.getItem("loginId") || '',
    name: '',
    email: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    pincode: '',
    permanentAddress: '',
    currentAddress: '',
    userType: 'Individual',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ ...profile });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const [error, setError] = useState(null);

  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const [demoRequests, setDemoRequests] = useState([]);

  const storedUserData = localStorage.getItem('userData');

  const [activities, setActivities] = useState([
    { id: 1, action: 'Profile Updated', time: '2 hours ago', icon: 'User' },
    { id: 2, action: 'Logged in from new device', time: '1 day ago', icon: 'Lock' },
    { id: 3, action: 'Demo requested for Keyword Tool', time: '3 days ago', icon: 'MessageSquare' },
    { id: 4, action: 'Subscription renewed', time: '5 days ago', icon: 'CreditCard' }
  ]);

  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: true
  });

  const [connectedAccounts, setConnectedAccounts] = useState({
    google: true,
    linkedin: true
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    username: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStep, setPasswordStep] = useState('request');
  const [otpSent, setOtpSent] = useState(false);

  const loadSubscription = async () => {
  try {
    setSubscriptionLoading(true);
    const data = await getMySubscription();
    setSubscription(data);
  } catch (error) {
    console.error('Error loading subscription:', error);
    setSubscription({
      plan_name: 'Free Plan',
      status: 'FREE'
    });
  } finally {
    setSubscriptionLoading(false);
  }
};


  // ✅ FIXED: Moved fetchUserDemoRequests BEFORE useEffect so it's defined
  const fetchUserDemoRequests = async () => {
    console.log('🔵 fetchUserDemoRequests called!');

    try {
      const token = localStorage.getItem('token');
      console.log('🔵 Token:', token ? 'EXISTS' : 'MISSING');

      if (!token) {
        console.log('❌ No token found for fetching demo requests');
        return;
      }

      console.log('🔵 Fetching user-specific demo requests...');
      console.log('🔵 API URL:', `${API_BASE_URL}/api/seo-user/demo-request/my-requests`);

      // ✅ FIXED: response is defined here from axios.get
      const response = await axios.get(`${API_BASE_URL}/api/seo-user/demo-request/my-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ API Response Status:', response.status);
      console.log('✅ API Response Data:', response.data);

      let apiData = [];
      if (Array.isArray(response.data)) {
        apiData = response.data;
        console.log('✅ Data is array, length:', apiData.length);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        apiData = response.data.data;
        console.log('✅ Data is nested array, length:', apiData.length);
      } else {
        console.log('⚠️ Unexpected response structure:', typeof response.data);
      }

      console.log('🔵 API Data to transform:', apiData);

      if (apiData.length === 0) {
        console.log('⚠️ No demo requests found for this user');
        setDemoRequests([]);
        return;
      }

      const transformedRequests = apiData.map((req, index) => {
        let productName = 'Demo Request';
        if (req.message && req.message.includes('Product:')) {
          const productMatch = req.message.match(/Product:\s*(.+?)(?:\n|$)/);
          if (productMatch) {
            productName = productMatch[1].trim();
          }
        }

        return {
          id: req.demoRequestId || index + 1,
          product: productName,
          date: new Date(req.createdDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }),
          status: 'pending',
          type: req.type || 'Individual',
          message: req.message || '',
          phone: req.phone || '',
          email: req.email || '',
          name: req.name || '',
          createdDate: req.createdDate
        };
      });

      transformedRequests.sort((a, b) =>
        new Date(b.createdDate) - new Date(a.createdDate)
      );

      console.log('✅ Transformed demo requests:', transformedRequests);
      console.log('🔵 Setting state with', transformedRequests.length, 'requests');
      setDemoRequests(transformedRequests);
      console.log('✅ State updated!');

    } catch (error) {
      console.error('❌ Error fetching demo requests:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);

      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('⚠️ Authentication failed - please login again');
      } else {
        console.log('⚠️ Using empty demo data due to API error');
        setDemoRequests([]);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    console.log('🔴 showDeleteModal:', showDeleteModal);
    console.log('🟡 showLogoutModal:', showLogoutModal);
    console.log('🔵 showPasswordModal:', showPasswordModal);


    const loadUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('No token found - redirecting to login');
          if (isMounted) {
            localStorage.clear();
            window.location.href = '/login';
          }
          return;
        }

        // ✅ NEW: Check if we should skip profile load (redirect scenario)
        const shouldSkip = sessionStorage.getItem('skipProfileLoad');
        if (shouldSkip === 'true') {
          console.log('⏭️ Skipping profile load - user is being redirected elsewhere');
          sessionStorage.removeItem('skipProfileLoad');
          return; // Exit early, don't load profile
        }

        // ✅ Normal flow continues - Set loading state
        if (isMounted) setLoading(true);

        // ✅ FIX 2: Don't use cached data initially - fetch fresh data first
        try {
          console.log('🔵 Fetching fresh profile data from API...');
          const userData = await profileService.getProfile();

          if (!userData) {
            throw new Error('Profile API returned empty');
          }

          console.log('✅ API returned data:', userData);

          // ✅ FIX 3: Create profile with database values (not defaults)
          const profileData = {
            loginId: userData.loginId || localStorage.getItem('loginId') || '',
            name: userData.name || '', // Don't use 'User' as default
            email: userData.email || '',
            phone: userData.phone ? String(userData.phone) : '',
            country: userData.country || '',
            state: userData.state || '',
            city: userData.city || '',
            pincode: userData.pincode ? String(userData.pincode) : '',
            permanentAddress: userData.permanentAddress || '',
            currentAddress: userData.currentAddress || '',
            userType: userData.userType || 'Individual',

          };

          // ✅ FIX 4: Only set state if we got valid data
          if (profileData.name && profileData.email) {
            console.log('✅ Setting profile with database data:', profileData);

            if (isMounted) {
              setProfile(profileData);
              setEditForm(profileData);
            }

            // ✅ FIX 5: Update localStorage with fresh database data
            localStorage.setItem('userData', JSON.stringify(profileData));
            if (profileData.loginId) {
              localStorage.setItem('loginId', profileData.loginId);
            }

            // ✅ FIX 6: Dispatch event to update navbar
            window.dispatchEvent(new Event("authChange"));

            // ✅ FIX 7: Fetch demo requests after profile is loaded
            await fetchUserDemoRequests();

            await loadSubscription();  

          } else {
            console.error('❌ Invalid profile data received - missing name or email');
            throw new Error('Invalid profile data');
          }

        } catch (apiError) {
          console.error('❌ Error fetching user profile:', apiError);

          // ✅ FIX 8: Handle authentication errors
          if (apiError.response?.status === 401 || apiError.response?.status === 403) {
            if (isMounted) {
              console.warn('Authentication failed - redirecting to login');
              localStorage.clear();
              window.location.href = '/login';
            }
            return;
          }

          // ✅ FIX 9: Only use cached data as fallback if API fails
          // AND only if cached data is valid (not "User")
          if (storedUserData) {
            try {
              const cachedData = JSON.parse(storedUserData);

              if (cachedData.name &&
                cachedData.name !== 'User' &&
                cachedData.email) {

                console.log('⚠️ API failed, using valid cached data as fallback');

                const cachedProfile = {
                  loginId: cachedData.loginId || '',
                  name: cachedData.name,
                  email: cachedData.email || '',
                  phone: cachedData.phone ? String(cachedData.phone) : '',
                  country: cachedData.country || '',
                  state: cachedData.state || '',
                  city: cachedData.city || '',
                  pincode: cachedData.pincode ? String(cachedData.pincode) : '',
                  permanentAddress: cachedData.permanentAddress || '',
                  currentAddress: cachedData.currentAddress || '',
                  userType: cachedData.userType || 'Individual',
                };

                if (isMounted) {
                  setProfile(cachedProfile);
                  setEditForm(cachedProfile);
                }
              } else {
                console.error('❌ No valid cached data available and API failed');
                // Could redirect to login or show error message
              }
            } catch (parseError) {
              console.error('Error parsing cached data:', parseError);
            }
          }
        }

      } catch (error) {
        console.error('❌ Unexpected error in loadUserData:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };



    loadUserData();

    return () => {
      isMounted = false;
    };


  }, []);


  const getStatusBadge = (status) => {
    return `status-badge status-${status}`;
  };

  const getStatusIcon = (status) => {
    if (status === 'approved') return <CheckCircle className="status-icon" />;
    if (status === 'rejected') return <XCircle className="status-icon" />;
    return <AlertCircle className="status-icon" />;
  };

  const getIcon = (iconName) => {
    const icons = {
      User: User,
      Lock: Lock,
      MessageSquare: MessageSquare,
      CreditCard: CreditCard
    };
    const IconComponent = icons[iconName] || Clock;
    return <IconComponent className="activity-icon" />;
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    setEditForm({
      ...profile,
      loginId: profile.loginId,
      country: profile.country || '',
      state: profile.state || '',
      city: profile.city || '',
      pincode: profile.pincode || '',
      permanentAddress: profile.permanentAddress || '',
      currentAddress: profile.currentAddress || ''
    });
  };

  const handleSaveProfile = async () => {
    if (loading) {
      console.log("⚠️ Save already in progress, ignoring duplicate call");
      return;
    }

    if (!editForm.name?.trim()) {
      showNotification('Name is required', 'error');
      return;
    }

    if (!editForm.email?.trim()) {
      showNotification('Email is required', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editForm.email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    if (editForm.phone?.trim()) {
      const cleanPhone = editForm.phone.toString().replace(/\D/g, '').replace(/^0+/, '');
      if (cleanPhone.length < 10 || cleanPhone.length > 15) {
        showNotification('Phone number must be 10-15 digits', 'error');
        return;
      }
    }

    if (editForm.pincode?.trim()) {
      const cleanPincode = editForm.pincode.toString().replace(/\D/g, '').replace(/^0+/, '');
      if (cleanPincode.length < 4 || cleanPincode.length > 10) {
        showNotification('Pincode must be 4-10 digits', 'error');
        return;
      }
    }

    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Session expired. Please login again.', 'error');
      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/login';
      }, 2000);
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone?.trim() || '',
        country: editForm.country?.trim() || '',
        state: editForm.state?.trim() || '',
        city: editForm.city?.trim() || '',
        pincode: editForm.pincode?.trim() || '',
        permanentAddress: editForm.permanentAddress?.trim() || '',
        currentAddress: editForm.currentAddress?.trim() || '',
        userType: editForm.userType || 'Individual'
      };

      console.log('📤 Updating profile with:', dataToSend);

      const updatedData = await profileService.updateProfile(dataToSend);

      console.log('✅ Profile update response:', updatedData);

      // ✅ FIX: Create the complete updated profile object
      const updatedProfile = {
        loginId: updatedData.loginId || profile.loginId || localStorage.getItem('loginId'),
        name: updatedData.name || dataToSend.name,
        email: updatedData.email || dataToSend.email,
        phone: updatedData.phone?.toString() || dataToSend.phone,
        country: updatedData.country || dataToSend.country,
        state: updatedData.state || dataToSend.state,
        city: updatedData.city || dataToSend.city,
        pincode: updatedData.pincode?.toString() || dataToSend.pincode,
        permanentAddress: updatedData.permanentAddress || dataToSend.permanentAddress,
        currentAddress: updatedData.currentAddress || dataToSend.currentAddress,
        userType: updatedData.userType || dataToSend.userType,
      };

      // ✅ FIX 1: Update state immediately
      setProfile(updatedProfile);
      setEditForm(updatedProfile);

      // ✅ FIX 2: Save to localStorage
      localStorage.setItem('userData', JSON.stringify(updatedProfile));
      if (updatedProfile.loginId) {
        localStorage.setItem('loginId', String(updatedProfile.loginId));
      }

      // ✅ FIX 3: Dispatch custom event to notify Navbar
      console.log('🔔 Dispatching authChange event');
      window.dispatchEvent(new Event("authChange"));

      setIsEditing(false);
      addActivity('Profile Updated', 'just now');
      showNotification('Profile updated successfully!', 'success');

      console.log('✅ Profile saved and event dispatched');

    } catch (error) {
      console.error('❌ Profile update error:', error);

      if (error.response) {
        const { status, data } = error.response;
        const errorTitle = data?.title?.toLowerCase() || '';
        const errorMessage = data?.message || data?.detail || '';

        console.log('Backend error:', { status, title: errorTitle, message: errorMessage });

        if (status === 401 || status === 403) {
          showNotification('Session expired. Please login again.', 'error');
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
          }, 2000);
        } else if (errorTitle === 'emailexists' || errorMessage.toLowerCase().includes('email already')) {
          showNotification('This email is already registered. Please use a different email.', 'error');
        } else if (errorTitle === 'usernotfound' || errorMessage.toLowerCase().includes('user not found')) {
          showNotification('User account not found. Please login again.', 'error');
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
          }, 2000);
        } else if (status === 400) {
          showNotification(
            errorMessage || 'Invalid data. Please check all fields and try again.',
            'error'
          );
        } else if (status === 500) {
          showNotification('Server error. Please try again later or contact support.', 'error');
        } else {
          showNotification(
            errorMessage || `Update failed (Error ${status}). Please try again.`,
            'error'
          );
        }
      } else if (error.request) {
        showNotification('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotification(error.message || 'An unexpected error occurred.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({ ...profile });
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/api/seo-user/changePasswordOtp`,
        {
          userName: passwordForm.username
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setOtpSent(true);
      setPasswordStep('verify');
      showNotification('OTP sent to your registered email!', 'success');

    } catch (error) {
      console.error('Error requesting OTP:', error);
      showNotification(
        error.response?.data?.message || 'Failed to send OTP',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };


  const handleValidateOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/api/seo-user/validateOtp`,
        {
          userName: passwordForm.username,
          otp: passwordForm.otp.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setPasswordStep('change');
      showNotification('OTP verified successfully!', 'success');

    } catch (error) {
      console.error('Error validating OTP:', error);
      showNotification(
        error.response?.data?.message || 'Invalid OTP',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };


  const handleChangePassword = async (e) => {
    e.preventDefault();

    // ✅ FIX 2: Add proper password validation
    if (!passwordForm.newPassword || !passwordForm.newPassword.trim()) {
      showNotification('Please enter a new password', 'error');
      return;
    }

    if (!passwordForm.confirmPassword || !passwordForm.confirmPassword.trim()) {
      showNotification('Please confirm your password', 'error');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showNotification('Passwords do not match!', 'error');
      return;
    }

    // ✅ FIX 3: Add password strength validation
    if (passwordForm.newPassword.length < 6) {
      showNotification('Password must be at least 6 characters', 'error');
      return;
    }

    setLoading(true);

    try {
      console.log('🔵 Changing password for:', passwordForm.username);

      // ✅ FIX 4: Match your backend DTO structure
      const response = await axios.post(
        `${API_BASE_URL}/api/seo-user/validateOtp`,
        {
          userName: passwordForm.username,
          otp: passwordForm.otp,  // ✅ Include OTP in password change request
          newPassword: passwordForm.newPassword.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setShowPasswordModal(false);
      setPasswordForm({ username: '', otp: '', newPassword: '', confirmPassword: '' });
      setPasswordStep('request');
      setOtpSent(false);
      showNotification('Password changed successfully!', 'success');

    } catch (error) {
      console.error('Error changing password:', error);
      showNotification(
        error.response?.data?.message || 'Failed to change password',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };


  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setPasswordForm({ ...passwordForm, username: profile.email });
  };

  const toggleNotification = (type) => {
    setNotifications(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    addActivity(`${type.charAt(0).toUpperCase() + type.slice(1)} notifications ${!notifications[type] ? 'enabled' : 'disabled'}`, 'just now');
  };

  const toggleAccount = (account) => {
    setConnectedAccounts(prev => ({
      ...prev,
      [account]: !prev[account]
    }));
    const accountName = account.charAt(0).toUpperCase() + account.slice(1);
    addActivity(`${accountName} ${!connectedAccounts[account] ? 'connected' : 'disconnected'}`, 'just now');
  };

  const handleUpgrade = () => {
    showNotification('Redirecting to upgrade page...', 'info');
    addActivity('Viewed upgrade options', 'just now');
  };







  const handleRaiseTicket = () => {
    setShowTicketModal(true);
  };

  const handleTicketSubmit = (e) => {
    e.preventDefault();
    setShowTicketModal(false);
    addActivity('Support ticket raised', 'just now');
    showNotification('Ticket submitted successfully! Our team will contact you soon.', 'success');
  };

  const handleViewTickets = () => {
    showNotification('Opening ticket history...', 'info');
    addActivity('Viewed ticket history', 'just now');
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
    setDeletePassword('');
    setDeleteError('');
  };

  const confirmDeleteAccount = async () => {
    setDeleteError('');

    // ✅ Validate password input
    if (!deletePassword || !deletePassword.trim()) {
      setDeleteError('Please enter your password to confirm deletion');
      return;
    }

    setIsDeleting(true);

    try {
      // ✅ Pass password to deleteAccount service
      await profileService.deleteAccount(deletePassword);

      setShowDeleteModal(false);
      addActivity('Account deletion requested', 'just now');
      showNotification('Account deleted successfully. Redirecting to login...', 'success');

      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/login';
      }, 2000);

    } catch (error) {
      console.error('Error deleting account:', error);

      // ✅ Handle specific error cases
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || error.response?.data?.title;
        if (errorMessage?.toLowerCase().includes('password')) {
          setDeleteError('Incorrect password. Please try again.');
        } else {
          setDeleteError(errorMessage || 'Failed to delete account');
        }
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setDeleteError('Session expired. Please login again.');
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        setDeleteError('Failed to delete account. Please try again.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    addActivity('Logged out', 'just now');
    setShowLogoutModal(false);
    showNotification('Logging out...', 'info');
    setTimeout(() => {
      localStorage.clear();
      window.location.href = '/login';
    }, 1500);
  };

  const addActivity = (action, time) => {
    const newActivity = {
      id: Date.now(),
      action,
      time,
      icon: 'User'
    };
    setActivities(prev => [newActivity, ...prev].slice(0, 10));
  };

  return (
    <div className="profile-page">
      {notification.show && (
        <div className={`notification-toast ${notification.type === 'success' ? 'toast-success' :
          notification.type === 'error' ? 'toast-error' : 'toast-info'
          }`}>
          {notification.message}
        </div>
      )}

      <div className="profile-container">
        <h1 className="page-title">My Profile</h1>

        {/* Profile Header */}
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar-icon">
                <User size={48} strokeWidth={1.5} />
              </div>
              <div className="online-indicator"></div>
            </div>

            {!isEditing ? (
              <div className="profile-info">
                <h2 className="profile-name">{profile.name || 'User'}</h2>
                <div className="profile-details">
                  <div className="profile-detail-item">
                    <Mail className="detail-icon" />
                    <span>{profile.email || 'Not provided'}</span>
                  </div>

                  <div className="profile-detail-item">
                    <Phone className="detail-icon" />
                    <span>
                      {profile.phone && String(profile.phone).trim() !== ''
                        ? String(profile.phone)
                        : 'Not provided'}
                    </span>
                  </div>

                  <div className="profile-detail-item">
                    <Building2 className="detail-icon" />
                    <span className="user-type-badge">
                      {profile.userType || 'Individual'}
                    </span>
                  </div>
                </div>

                {(profile.city || profile.state || profile.country) && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <svg
                      style={{ width: '16px', height: '16px', color: '#667eea' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {[profile.city, profile.state, profile.country].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
            ) : (
              <div className="profile-edit-form">
                <div className="form-group">
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    <User size={16} /> Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    <Mail size={16} /> Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    <Phone size={16} /> Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={editForm.phone || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setEditForm({ ...editForm, phone: value });
                    }}
                    className="edit-input"
                    placeholder="Enter 10-15 digit phone number"
                    maxLength="15"
                  />
                  <small style={{
                    color: '#6B7280',
                    fontSize: '0.85rem',
                    marginTop: '0.25rem',
                    display: 'block'
                  }}>
                    Enter 10-15 digits without spaces or special characters
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#374151', display: 'block' }}>
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={editForm.country || ''}
                    onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your country"
                  />
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#374151', display: 'block' }}>
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={editForm.state || ''}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your state"
                  />
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#374151', display: 'block' }}>
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={editForm.city || ''}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your city"
                  />
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#374151', display: 'block' }}>
                    Pincode
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={editForm.pincode || ''}
                    onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your pincode"
                  />
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#374151', display: 'block' }}>
                    Permanent Address
                  </label>
                  <textarea
                    name="permanentAddress"
                    value={editForm.permanentAddress || ''}
                    onChange={(e) => setEditForm({ ...editForm, permanentAddress: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your permanent address"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#374151', display: 'block' }}>
                    Current Address
                  </label>
                  <textarea
                    name="currentAddress"
                    value={editForm.currentAddress || ''}
                    onChange={(e) => setEditForm({ ...editForm, currentAddress: e.target.value })}
                    className="edit-input"
                    placeholder="Enter your current address"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    <Building2 size={16} /> Account Type *
                  </label>
                  <select
                    name="userType"
                    value={editForm.userType || 'Individual'}
                    onChange={(e) => setEditForm({ ...editForm, userType: e.target.value })}
                    className="edit-input"
                    required
                  >
                    <option value="Individual">Individual</option>
                    <option value="Organisation">Organisation</option>
                  </select>
                </div>
              </div>
            )}

            <div className="profile-actions">
              {!isEditing ? (
                <button onClick={handleEditProfile} className="btn-primary">
                  <Edit className="btn-icon" />
                  Edit Profile
                </button>
              ) : (
                <div className="edit-actions-container" style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={handleSaveProfile}
                    className="btn-primary"
                    disabled={loading}
                  >
                    <Save className="btn-icon" />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>

                  <button
                    onClick={handleCancelEdit}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    <X className="btn-icon" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Settings */}
        <div className="profile-card">
          <h3 className="section-title">Account Settings</h3>
          <div className="settings-grid">
            <div className="setting-card" onClick={openPasswordModal}>
              <div className="setting-header">
                <div className="setting-icon-wrapper blue">
                  <Lock className="setting-icon" />
                </div>
                <h4 className="setting-title">Change Password</h4>
              </div>
              <p className="setting-description">Update your password regularly for security</p>
              <button className="setting-link">Update Password →</button>
            </div>

            {/* <div className="setting-card">
              <div className="setting-header">
                <div className="setting-icon-wrapper purple">
                  <Bell className="setting-icon" />
                </div>
                <h4 className="setting-title">Notifications</h4>
              </div>
              <div className="notification-toggles">
                {Object.entries(notifications).map(([key, value]) => (
                  <div key={key} className="toggle-item">
                    <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={() => toggleNotification(key)}
                    />
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        </div>


       
        {/* <div className="profile-card">
          <h3 className="section-title">Subscription & Plan</h3>

          {subscriptionLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <RefreshCw size={24} className="spinner" />
              <p>Loading subscription...</p>
            </div>
          ) : subscription ? (
            <div className="subscription-card">
              <div className="subscription-content">
                <div className="subscription-info">
                  <div className="subscription-header">
                    <CreditCard className="subscription-icon" />
                    <h4 className="subscription-plan">{subscription.plan_name}</h4>
                    <span className={`status-badge status-${subscription?.status?.toLowerCase()}`}>
                      {subscription.status}
                    </span>
                  </div>

                  {subscription.billing_cycle && (
                    <div className="billing-info">
                      <Calendar className="billing-icon" />
                      <span>
                        Billing: {subscription.billing_cycle}
                        {subscription.end_date && ` • Next: ${new Date(subscription.end_date).toLocaleDateString()}`}
                      </span>
                    </div>
                  )}

                  <div className="feature-tags">
                    {Array.isArray(subscription.features) && subscription.features.map((feature, idx) => (
                      <span key={idx} className="feature-tag">{feature}</span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedProductId(subscription.product_id);
                    setShowDemoModal(true);
                  }}
                  className="btn-upgrade"
                >
                  <TrendingUp className="btn-icon" />
                  {subscription.status === 'FREE' || subscription.status === 'EXPIRED'
                    ? 'Request Demo'
                    : 'Upgrade Plan'}
                </button>
              </div>
            </div>
          ) : (
            <p>Unable to load subscription data</p>
          )}
        </div> */}

        {/* Demo Requests */}
        <div className="profile-card">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h3 className="section-title">My Demo Requests</h3>
            <button
              onClick={fetchUserDemoRequests}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: loading ? '#9ca3af' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => !loading && (e.currentTarget.style.background = '#5568d3')}
              onMouseOut={(e) => !loading && (e.currentTarget.style.background = '#667eea')}
            >
              <RefreshCw size={16} />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {demoRequests.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              background: '#f9fafb',
              borderRadius: '12px',
              border: '2px dashed #e5e7eb'
            }}>
              <MessageSquare size={48} style={{ color: '#9ca3af', margin: '0 auto 1rem' }} />
              <p style={{ color: '#6b7280', fontSize: '1rem', marginBottom: '0.5rem' }}>
                No demo requests yet
              </p>
              <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                Request a demo for any product to see it here
              </p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="demo-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Request Type</th>
                    <th>Date Requested</th>
                    <th>Status</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {demoRequests.map((request) => (
                    <tr key={request.id}>
                      <td className="product-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <TrendingUp size={16} style={{ color: '#667eea' }} />
                          {request.product}
                        </div>
                      </td>
                      <td>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: request.type === 'Enterprise' ? '#fef3c7' :
                            request.type === 'Organisation' ? '#dbeafe' : '#e0e7ff',
                          color: request.type === 'Enterprise' ? '#92400e' :
                            request.type === 'Organisation' ? '#1e40af' : '#3730a3',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}>
                          {request.type}
                        </span>
                      </td>
                      <td className="request-date">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Calendar size={14} style={{ color: '#9ca3af' }} />
                          {request.date}
                        </div>
                      </td>
                      <td>
                        <span className={getStatusBadge(request.status)}>
                          {getStatusIcon(request.status)}
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </td>
                      <td>
                        <div style={{
                          maxWidth: '300px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: '#6b7280',
                          fontSize: '0.9rem'
                        }}>
                          {request.message ?
                            request.message.replace(/^Product:.*\n\n/, '').substring(0, 50) +
                            (request.message.length > 50 ? '...' : '') :
                            'No message'
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity Log */}
        {/* <div className="profile-card">
          <h3 className="section-title">Activity Log</h3>
          <div className="activity-list">
            {activities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon-wrapper">
                  {getIcon(activity.icon)}
                </div>
                <div className="activity-content">
                  <p className="activity-action">{activity.action}</p>
                  <div className="activity-time">
                    <Clock className="time-icon" />
                    <span>{activity.time}</span>
                  </div>
                </div>
              </div>

              
            ))}
          </div>
        </div> */}

        {/* Danger Zone */}
        <div className="profile-card">
          <h3
            className="section-title"
            style={{ color: '#dc2626', marginBottom: '1rem' }}
          >
            Danger Zone
          </h3>

          <p
            style={{
              color: '#6b7280',
              fontSize: '0.95rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
            }}
          >
            Irreversible and destructive actions. Please proceed with caution.
          </p>

          <div className="settings-grid">
            {/* Delete Account */}
            <div
              className="setting-card"
              onClick={() => setShowDeleteModal(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className="setting-header">
                <div
                  className="setting-icon-wrapper"
                  style={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  }}
                >
                  <Trash2 className="setting-icon" />
                </div>
                <h4 className="setting-title">Delete Account</h4>
              </div>

              <p className="setting-description">
                Permanently delete your account and all associated data
              </p>

              <button className="setting-link" style={{ color: '#dc2626' }}>
                Delete Account →
              </button>
            </div>

            {/* Logout */}
            <div
              className="setting-card"
              onClick={handleLogout}
              style={{ cursor: 'pointer' }}
            >
              <div className="setting-header">
                <div
                  className="setting-icon-wrapper"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  <LogOut className="setting-icon" />
                </div>
                <h4 className="setting-title">Logout</h4>
              </div>

              <p className="setting-description">
                Sign out of your account on this device
              </p>

              <button className="setting-link">
                Logout →
              </button>
            </div>
          </div>
        </div>



      </div>



      {/* Password Change Modal */}
      {showPasswordModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => !loading && setShowPasswordModal(false)}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              zIndex: 1000000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: '#667eea', marginBottom: '1.5rem' }}>Change Password</h2>

            {passwordStep === 'request' && (
              <form onSubmit={handleRequestOTP}>
                <div style={{ marginBottom: '1rem' }}>
                  <label>Email/Username</label>
                  <input
                    type="text"
                    value={passwordForm.username}
                    onChange={(e) => setPasswordForm({ ...passwordForm, username: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              </form>
            )}

            {passwordStep === 'verify' && (
              <form onSubmit={handleValidateOTP}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    value={passwordForm.otp}
                    onChange={(e) => {
                      // ✅ Only allow numbers
                      const value = e.target.value.replace(/\D/g, '');
                      setPasswordForm({ ...passwordForm, otp: value });
                    }}
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      letterSpacing: '0.5rem',
                      textAlign: 'center'
                    }}
                  />
                  <p style={{
                    marginTop: '0.5rem',
                    fontSize: '0.85rem',
                    color: '#6b7280'
                  }}>
                    Check your email for the OTP code
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading || !passwordForm.otp}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: (loading || !passwordForm.otp) ? '#9ca3af' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (loading || !passwordForm.otp) ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </form>
            )}

            {passwordStep === 'change' && (
              <form onSubmit={handleChangePassword}>
                {/* Hidden OTP field to pass to backend */}
                <input type="hidden" value={passwordForm.otp} />

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="Enter new password (min 6 characters)"
                    minLength="6"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    minLength="6"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !passwordForm.newPassword || !passwordForm.confirmPassword}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: (loading || !passwordForm.newPassword || !passwordForm.confirmPassword)
                      ? '#9ca3af' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (loading || !passwordForm.newPassword || !passwordForm.confirmPassword)
                      ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowLogoutModal(false)}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '16px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              zIndex: 1000000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <LogOut size={24} style={{ color: 'white' }} />
              </div>
              <div>
                <h2 style={{
                  color: '#667eea',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  margin: 0
                }}>
                  Logout Confirmation
                </h2>
                <p style={{
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  margin: '0.25rem 0 0 0'
                }}>
                  Are you sure you want to logout?
                </p>
              </div>
            </div>

            <p style={{
              color: '#6b7280',
              fontSize: '0.95rem',
              lineHeight: '1.6',
              marginBottom: '1.5rem'
            }}>
              You will need to log in again to access your account.
            </p>

            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>

              <button
                onClick={confirmLogout}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.3)'
                }}
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}

      {showDeleteModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            if (!isDeleting) {
              setShowDeleteModal(false);
              setDeletePassword('');
              setDeleteError('');
            }
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              zIndex: 1000000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Trash2 size={24} style={{ color: 'white' }} />
              </div>
              <div>
                <h2 style={{
                  color: '#dc2626',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  margin: 0
                }}>
                  Delete Account
                </h2>
                <p style={{
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  margin: '0.25rem 0 0 0'
                }}>
                  This action cannot be undone
                </p>
              </div>
            </div>

            {/* Warning Message */}
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                color: '#991b1b',
                fontSize: '0.9rem',
                lineHeight: '1.6',
                margin: 0
              }}>
                ⚠️ This will permanently delete your account and remove all your data from our servers.
                You will lose access to all your demo requests, activity history, and subscription benefits.
              </p>
            </div>

            {/* Password Input */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: '#374151',
                fontSize: '0.95rem'
              }}>
                Enter your password to confirm:
              </label>
              <input
                type="password"
                placeholder="Enter your password"
                value={deletePassword}
                onChange={(e) => {
                  setDeletePassword(e.target.value);
                  setDeleteError(''); // Clear error when typing
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isDeleting) {
                    confirmDeleteAccount();
                  }
                }}
                disabled={isDeleting}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: deleteError ? '2px solid #dc2626' : '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.2s',
                  backgroundColor: isDeleting ? '#f3f4f6' : 'white',
                  cursor: isDeleting ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!deleteError) {
                    e.target.style.borderColor = '#667eea';
                  }
                }}
                onBlur={(e) => {
                  if (!deleteError) {
                    e.target.style.borderColor = '#e5e7eb';
                  }
                }}
              />

              {/* Error Message */}
              {deleteError && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '0.5rem',
                  color: '#dc2626',
                  fontSize: '0.875rem'
                }}>
                  <AlertCircle size={16} />
                  <span>{deleteError}</span>
                </div>
              )}

              <p style={{
                marginTop: '0.5rem',
                fontSize: '0.85rem',
                color: '#6b7280'
              }}>
                We need your password to verify this critical action
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteError('');
                }}
                disabled={isDeleting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  opacity: isDeleting ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }
                }}
              >
                Cancel
              </button>

              <button
                onClick={confirmDeleteAccount}
                disabled={isDeleting || !deletePassword}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: (isDeleting || !deletePassword)
                    ? '#9ca3af'
                    : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                  color: 'white',
                  cursor: (isDeleting || !deletePassword) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                  boxShadow: (!isDeleting && deletePassword)
                    ? '0 4px 6px -1px rgba(220, 38, 38, 0.3)'
                    : 'none'
                }}
                onMouseOver={(e) => {
                  if (!isDeleting && deletePassword) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 8px -1px rgba(220, 38, 38, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isDeleting && deletePassword) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(220, 38, 38, 0.3)';
                  }
                }}
              >
                {isDeleting ? (
                  <>
                    <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete My Account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Demo Request Modal */}
      <DemoRequestModal
        isOpen={showDemoModal}
        onClose={() => {
          setShowDemoModal(false);
          setSelectedProductId(null);
        }}
        productName={subscription?.product_name || subscription?.plan_name}
        productId={selectedProductId}
      />

      <SecondaryFooter />

    </div>
  );
};

export default ProfilePage;